import type { CompanyInventoryRepository } from "../../repositories/company/company-inventory-repository";
import type {
  MaterialInventoryDto,
  InventoryRequest,
  InventoryLogPageResponse,
} from "../../types/company/company-product-system";
import { AppError } from "../../../shared/utils/error-handler";

export class CompanyInventoryUseCase {
  constructor(private repository: CompanyInventoryRepository) {}

  async getAllInventory(companyId: number, type?: string): Promise<MaterialInventoryDto[]> {
    try {
      return await this.repository.getAllInventory(companyId, type);
    } catch (error) {
      throw new AppError(
        String(error) || "재고 목록을 불러오는데 실패했습니다.",
        "INVENTORY_LIST_FETCH_FAILED",
        error,
      );
    }
  }

  async getInventory(companyId: number, materialId: number): Promise<MaterialInventoryDto> {
    try {
      return await this.repository.getInventory(companyId, materialId);
    } catch (error) {
      throw new AppError(
        String(error) || "재고 정보를 불러오는데 실패했습니다.",
        "INVENTORY_FETCH_FAILED",
        error,
      );
    }
  }

  async receive(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<MaterialInventoryDto> {
    try {
      if (request.quantity <= 0) {
        throw new Error("입고 수량은 1 이상이어야 합니다.");
      }
      return await this.repository.receive(companyId, materialId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "입고 처리에 실패했습니다.",
        "INVENTORY_RECEIVE_FAILED",
        error,
      );
    }
  }

  async deduct(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<MaterialInventoryDto> {
    try {
      if (request.quantity <= 0) {
        throw new Error("차감 수량은 1 이상이어야 합니다.");
      }
      return await this.repository.deduct(companyId, materialId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "차감 처리에 실패했습니다.",
        "INVENTORY_DEDUCT_FAILED",
        error,
      );
    }
  }

  async returnStock(
    companyId: number,
    materialId: number,
    request: InventoryRequest,
  ): Promise<MaterialInventoryDto> {
    try {
      if (request.quantity <= 0) {
        throw new Error("반품 수량은 양수여야 합니다.");
      }
      return await this.repository.returnStock(companyId, materialId, request);
    } catch (error) {
      throw new AppError(
        String(error) || "반품 처리에 실패했습니다.",
        "INVENTORY_RETURN_FAILED",
        error,
      );
    }
  }

  async getLogs(
    companyId: number,
    materialId: number,
    page = 0,
    size = 20,
  ): Promise<InventoryLogPageResponse> {
    try {
      return await this.repository.getLogs(companyId, materialId, page, size);
    } catch (error) {
      throw new AppError(
        String(error) || "재고 이력을 불러오는데 실패했습니다.",
        "INVENTORY_LOGS_FETCH_FAILED",
        error,
      );
    }
  }
}
